import React, { useState, useEffect, useMemo } from 'react';
import { FilterPanel } from './components/FilterPanel';
import { SavingTable, DailyReportData } from './components/SavingTable';
import { SummaryBox } from './components/SummaryBox';
import { AutopilotGraph, DailyData } from './components/AutopilotGraph';
import { SolarStackBar, EnergyData } from './components/SolarStackBar';
import { 
  parseCSV, 
  loadCSVFile, 
  DailyDataRow, 
  ReportDailyRow, 
  ReportMonthlyRow 
} from './utils/csvParser';

export function SavingsPage() {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [dailyData, setDailyData] = useState<DailyDataRow[]>([]);
  const [reportDaily, setReportDaily] = useState<ReportDailyRow[]>([]);
  const [reportMonthly, setReportMonthly] = useState<ReportMonthlyRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading CSV files...');
        const [dailyCSV, reportDailyCSV, reportMonthlyCSV] = await Promise.all([
          loadCSVFile('/src/pages/savings/mock-data/daily_data_AYT.csv'),
          loadCSVFile('/src/pages/savings/mock-data/report_daily_ATY.csv'),
          loadCSVFile('/src/pages/savings/mock-data/report_monthly_ATY.csv'),
        ]);

        console.log('Parsing CSV data...');
        const [daily, reportD, reportM] = await Promise.all([
          parseCSV<DailyDataRow>(dailyCSV),
          parseCSV<ReportDailyRow>(reportDailyCSV),
          parseCSV<ReportMonthlyRow>(reportMonthlyCSV),
        ]);

        console.log('Data loaded:', { 
          daily: daily.length, 
          reportDaily: reportD.length, 
          reportMonthly: reportM.length 
        });

        setDailyData(daily);
        setReportDaily(reportD);
        setReportMonthly(reportM);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading CSV data:', error);
        setError(error instanceof Error ? error.message : 'Failed to load data');
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    reportDaily.forEach(row => {
      const year = new Date(row.Date).getFullYear();
      if (!isNaN(year)) years.add(year);
    });
    return Array.from(years).sort();
  }, [reportDaily]);

  const availableMonths = useMemo(() => {
    if (!selectedYear) return [];
    
    const months = new Set<number>();
    reportDaily.forEach(row => {
      const date = new Date(row.Date);
      if (date.getFullYear() === selectedYear) {
        months.add(date.getMonth());
      }
    });
    
    return Array.from(months).sort((a, b) => b - a).map(m => ({
      value: m,
      label: new Date(2000, m).toLocaleString('en', { month: 'long' })
    }));
  }, [selectedYear, reportDaily]);

  const filteredData = useMemo(() => {
    let filteredDaily = dailyData;
    let filteredReport = reportDaily;

    if (selectedYear) {
      filteredDaily = filteredDaily.filter(row => {
        const year = new Date(row.datetime).getFullYear();
        return year === selectedYear;
      });
      filteredReport = filteredReport.filter(row => {
        const year = new Date(row.Date).getFullYear();
        return year === selectedYear;
      });
    }

    if (selectedMonth !== null && selectedYear) {
      filteredDaily = filteredDaily.filter(row => {
        const date = new Date(row.datetime);
        return date.getMonth() === selectedMonth;
      });
      filteredReport = filteredReport.filter(row => {
        const date = new Date(row.Date);
        return date.getMonth() === selectedMonth;
      });
    }

    return { daily: filteredDaily, report: filteredReport };
  }, [dailyData, reportDaily, selectedYear, selectedMonth]);

  const tableData: DailyReportData[] = filteredData.report.map(row => ({
    date: row.Date,
    outdoorTemp: row['Outdoor Temp (°C)'],
    baselineAC: row['Baseline AC (kWh)'],
    actualAC: row['Actual AC (kWh)'],
    savingKwh: row['Autopilot Saving (kWh)'],
    savingPercent: row['Autopilot Saving (%)'],
    solar: row['Solar (kWh)'],
    grid: row['Grid (kWh)'],
    solarPercentage: row['Solar Percentage (%)'],
  }));

  const summaryData = useMemo(() => {
    const totalConsumption = filteredData.report.reduce((sum, row) => sum + row['Total (kWh)'], 0);
    const gridImport = filteredData.report.reduce((sum, row) => sum + row['Grid (kWh)'], 0);
    const solarConsumption = filteredData.report.reduce((sum, row) => sum + row['Solar (kWh)'], 0);
    const autopilotSavings = filteredData.report.reduce((sum, row) => sum + row['Autopilot Saving (kWh)'], 0);
    const peakDemand = Math.max(...filteredData.daily.map(row => row.active_power_kw || 0));
    const reportingDays = filteredData.report.length;
    const avgDailyConsumption = reportingDays > 0 ? totalConsumption / reportingDays : 0;
    
    // Calculate percentages based on grid + solar total (energy sources)
    const energySourceTotal = gridImport + solarConsumption;
    const gridPercent = energySourceTotal > 0 ? (gridImport / energySourceTotal) * 100 : 0;
    const solarPercent = energySourceTotal > 0 ? (solarConsumption / energySourceTotal) * 100 : 0;
    
    // Calculate savings percentage based on baseline
    const totalBaseline = filteredData.report.reduce((sum, row) => sum + row['Baseline AC (kWh)'], 0);
    const savingsPercent = totalBaseline > 0 ? (autopilotSavings / totalBaseline) * 100 : 0;

    // Calculate date range
    let startDate = '';
    let endDate = '';
    if (filteredData.report.length > 0) {
      const dates = filteredData.report.map(row => new Date(row.Date)).sort((a, b) => a.getTime() - b.getTime());
      startDate = dates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      endDate = dates[dates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    return {
      totalConsumption,
      gridImport,
      solarConsumption,
      autopilotSavings,
      peakDemand,
      avgDailyConsumption,
      gridPercent,
      solarPercent,
      savingsPercent,
      totalBaseline,
      totalDays: reportingDays,
      dateRange: startDate && endDate ? `${startDate} - ${endDate}` : '',
    };
  }, [filteredData]);

  const autopilotData: DailyData[] = filteredData.daily.map(row => ({
    datetime: row.datetime,
    outdoorTemp: row.outdoor_temp_c,
    acKwh: row.ac_kwh,
    operation: row.operation,
    indoorTemp: row.indoor_temp_c,
    ahuWorkingPercent: row.ahu_working_percent,
  }));

  const energyData: EnergyData[] = filteredData.daily.map(row => ({
    datetime: row.datetime,
    solar: row.solar_kwh,
    grid: row.grid_kwh,
    indoorTemp: row.indoor_temp_c,
    outdoorTemp: row.outdoor_temp_c,
    ahuWorkingPercent: row.ahu_working_percent,
    operation: row.operation,
  }));

  if (isLoading) {
    return (
      <div className="absolute inset-0 flex justify-center items-center bg-bg">
        <div className="text-center">
          <div className="flex gap-1 justify-center mb-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-breathe-dots"></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-breathe-dots"></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-breathe-dots"></div>
          </div>
          <p className="paragraph-12 text-dark-grey">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute inset-0 flex justify-center items-center bg-bg">
        <div className="text-center bg-white p-6 rounded-lg shadow-sm border border-light-grey">
          <p className="heading-6 text-danger mb-2">Error loading data</p>
          <p className="paragraph-12 text-dark-grey">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col p-4 pb-6 overflow-hidden bg-bg">
      <div className="flex-shrink-0 h-10 mb-2">
        <div className="grid grid-cols-12 gap-3 h-full">
          <div className="col-span-4">
            <FilterPanel
              onYearChange={setSelectedYear}
              onMonthChange={setSelectedMonth}
              availableYears={availableYears}
              availableMonths={availableMonths}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-12 gap-3 min-h-0">
        <div className="col-span-5 flex flex-col gap-3 min-h-0">
          <div className="min-h-0" style={{ flex: '0 0 65%' }}>
            <SavingTable data={tableData} />
          </div>
          <div className="min-h-0" style={{ flex: '0 0 35%' }}>
            <SummaryBox {...summaryData} />
          </div>
        </div>

        <div className="col-span-7 flex flex-col gap-3 min-h-0">
          <div className="min-h-0" style={{ flex: '0 0 50%' }}>
            <AutopilotGraph data={autopilotData} />
          </div>
          <div className="min-h-0" style={{ flex: '0 0 50%' }}>
            <SolarStackBar data={energyData} />
          </div>
        </div>
      </div>
    </div>
  );
}
